import { Radio } from "frontend/src/components/Radio/Radio";
import Button from "frontend/src/components/Button/Button";
import Icon from "frontend/src/icons/Icon";
import { toggleExpandedFavoritesSidebar } from "_sirius/src/actions/general_actions";
import { useMutation } from "@apollo/client";

import { Tag } from "frontend/src/components/Tag/Tag";
import { RangerChat } from "frontend/src/components/RangerChat";
import { useCurrentUser } from "frontend/src/utils/hooks";
import FavoritesList from "Components/FavoritesPanel/FavoritesList";
import InvestmentSetForm from "Components/InvestmentSetForm";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useParams } from "react-router-dom";
import { heightValue } from "Selectors/header_selectors";
import * as types from "_graphql-types/graphql";
import { UserActivityInput } from "_graphql-types/graphql";
import { gql } from "_graphql-types/gql";
import { styled, useTheme } from "@mui/material/styles";
import { useDebounce } from "Helpers/hooks";
import useSidebarStore, { PannelEnum } from "./sidebarStore";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import OpenInFullIcon from "@mui/icons-material/OpenInFull";
import CloseFullscreenIcon from "@mui/icons-material/CloseFullscreen";
import config from "../../config/config";
import Tooltip from "frontend/src/components/Tooltip/Tooltip";
const StyledListHeaderDiv = styled("div")({
  alignItems: "center",
  display: "flex",
  flexDirection: "row",
  justifyContent: "center",
  flexGrow: 1,
});

const ADD_USER_ACTIVITY = gql(/* GraphQL */ `
  mutation SidebarAddUserActivity($input: UserActivityInput!) {
    addUserActivity(input: $input) {
      id
    }
  }
`);

function SideBar() {
  const theme = useTheme();
  const [isFocused, setIsFocused] = useState(false);

  const params = useParams();
  const [entityId, entityType] = useMemo(() => {
    if (params.investmentId) {
      return [Number(params.investmentId), types.ChatEntityType.Investment];
    } else if (params.companyId) {
      return [Number(params.companyId), types.ChatEntityType.Company];
    } else if (params.firmId) {
      return [Number(params.firmId), types.ChatEntityType.Firm];
    }
    return [undefined, undefined];
  }, [params]);

  const dispatch = useDispatch();
  const { investmentId, companyId, firmId, portfolioId } = useParams();
  const navBarHeight = useSelector(heightValue);
  const favoritesSidebarExpanded = useSelector(
    (state: { app: { favoritesSidebarExpanded: boolean } }) => state.app.favoritesSidebarExpanded
  );
  const caretDirection = favoritesSidebarExpanded ? "right" : "left";
  const panelState = favoritesSidebarExpanded ? "shown" : "hidden";

  const [action, setAction] = useState(false);
  const [panelSize, setPanelSize] = useState("small");

  const currentPanel = useSidebarStore(state => state.currentPanel);
  const setCurrentPanel = useSidebarStore(state => state.setCurrentPanel);
  const isFullscreen = useSidebarStore(state => state.isFullscreen);
  const setIsFullscreen = useSidebarStore(state => state.setIsFullscreen);

  const currentUser = useCurrentUser();
  const [addUserActivity] = useMutation(ADD_USER_ACTIVITY);

  const toggleFullscreen = useCallback(() => {
    const next = !isFullscreen;
    setIsFullscreen(next);

    const appVersion = config.SENTRY_RELEASE();
    if (currentUser.email && appVersion) {
      const input: UserActivityInput = {
        email: currentUser.email,
        applicationId: config.SIRIUS_APP_ID(),
        rcgApplicationVersion: appVersion,
        requestUri: "sidebar-fullscreen-toggle",
        requestUtc: new Date().toISOString(),
      };
      addUserActivity({ variables: { input } });
    }
  }, [isFullscreen, setIsFullscreen, currentUser.email, addUserActivity]);

  if (favoritesSidebarExpanded) {
    document.body.classList.add("favorites-panel-shown");
  } else {
    document.body.classList.remove("favorites-panel-shown");
  }

  const Pannels = useMemo<
    {
      key: PannelEnum;
      title: string | JSX.Element;
      body: ({
        buttonAction,
        setButtonAction,
      }: {
        buttonAction?: boolean;
        setButtonAction?: (value: boolean) => void;
      }) => JSX.Element;
      button?: { iconClass: string };
      exclude?: boolean;
      expandOnFocus?: boolean;
    }[]
  >(
    () => [
      {
        key: PannelEnum.FavoritesList,
        title: "Lists",
        body: ({
          buttonAction,
          setButtonAction,
        }: {
          buttonAction?: boolean;
          setButtonAction?: (value: boolean) => void;
        }) => {
          return (
            <>
              <FavoritesList />
              {buttonAction && (
                <InvestmentSetForm
                  handleCancel={() => {
                    setButtonAction && setButtonAction(false);
                  }}
                  visible={!!buttonAction}
                />
              )}
            </>
          );
        },
        button: {
          iconClass: "icon icon-plus",
        },
      },
      {
        key: PannelEnum.RangerChat,
        title: (
          // no explicit font-size here — inherit the ToggleButton typography so this
          // tab matches the plain-text "Lists" tab exactly. Icon sized to the label.
          <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
            Sirius AI
            <Tooltip title="AI-powered">
              <AutoAwesomeIcon color="primary" sx={{ fontSize: 16 }} />
            </Tooltip>
          </span>
        ),
        expandOnFocus: true,
        body: () => {
          return <RangerChat params={{ investmentId, companyId, firmId, portfolioId }} isFullscreen={isFullscreen} />;
        },
      },
    ],
    [investmentId, companyId, firmId, portfolioId, setPanelSize, isFullscreen]
  );

  const current = Pannels.find(({ key }) => key === currentPanel);

  // defer action to not conflict with panel change events occurring at the same time as focus event
  const debouncedFocus = useDebounce(isFocused, 50);

  useEffect(() => {
    if (isFullscreen) return;
    if (favoritesSidebarExpanded) {
      if (isFocused) {
        if (current?.expandOnFocus) {
          setPanelSize("large");
        } else {
          setPanelSize("small");
        }
      } else {
        setPanelSize("small");
      }
    }
  }, [debouncedFocus, favoritesSidebarExpanded, setPanelSize, current, isFullscreen]);

  useEffect(() => {
    if (panelSize === "large" || isFullscreen) {
      // Delay focus slightly to allow the panel expansion/fullscreen animation to start
      requestAnimationFrame(() => {
        const input = document.getElementById("sidebar-chat-input") as HTMLTextAreaElement | null;
        input?.focus();
      });
    }
  }, [panelSize, isFullscreen]);

  // Any route change while in fullscreen exits fullscreen. Navigation from
  // the app chrome (global-search results, left-nav links, the logo) happens
  // programmatically via useNavigate() — there is no <a> and no click inside
  // the sidebar to intercept — so without this the new page renders hidden
  // under the fullscreen chat overlay and navigating appears to do nothing.
  const location = useLocation();
  const prevLocationKey = useRef(location.key);
  useEffect(() => {
    if (location.key === prevLocationKey.current) return;
    prevLocationKey.current = location.key;
    if (isFullscreen) {
      setIsFullscreen(false);
    }
  }, [location.key, isFullscreen, setIsFullscreen]);

  // Clicking the app chrome that stays visible in fullscreen (top navbar,
  // left navigation) exits fullscreen immediately — the user is starting to
  // do something else. Scoped to the chrome rather than "anything outside
  // the sidebar" because chat's own popups (Ant/MUI portals) render into
  // document.body and must not trigger an exit (see the onBlur note below).
  // Capture phase so the persisted store is written before the click's own
  // handler navigates or opens anything.
  useEffect(() => {
    if (!isFullscreen) return;
    const exitOnChromeClick = (e: MouseEvent) => {
      if (
        e.target instanceof Element &&
        e.target.closest("#navbar, .nav-sidebar__wrap")
      ) {
        setIsFullscreen(false);
      }
    };
    document.addEventListener("click", exitOnChromeClick, true);
    return () => document.removeEventListener("click", exitOnChromeClick, true);
  }, [isFullscreen, setIsFullscreen]);

  // The fullscreen chat is a fixed overlay covering the workspace. Like a modal,
  // it must lock background page scroll while open. `custom_reset.scss` makes
  // `body` exactly one viewport tall with a permanent `overflow-y: scroll`
  // track, and the routed page behind the overlay is normal-flow content that
  // can be taller than the viewport (correctly scrollable when it IS the page).
  // Left unlocked, that background scrollbar shows at the viewport's right edge
  // stacked beside the chat's own Conversation-History scrollbar (the reported
  // "double scrollbar"), and every MUI popover opened in the chat toggles it
  // (MUI's scroll-lock hides/reveals it — the reported "disappears when the
  // settings popover opens"). Locking body scroll while fullscreen removes the
  // background scrollbar entirely, leaving only the chat's internal scrollers.
  useEffect(() => {
    if (!isFullscreen) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isFullscreen]);

  return (
    <div
      id="main-sidebar"
      data-cy="main-sidebar"
      className={`main-sidebar ml-10 main-sidebar--${panelState} ${
        isFullscreen
          ? "main-sidebar__fullscreen"
          : panelSize === "large"
          ? "main-sidebar__large"
          : ""
      }
      ${!isFullscreen && panelSize === "small" ? "main-sidebar__small" : ""}`}
      style={{
        height: `calc(100% - ${navBarHeight}px)`,
        top: `${navBarHeight}px`,
      }}
      tabIndex={0}
      onTransitionEnd={e => {
        // The panel grows on focus and shrinks on blur, which can happen with
        // one of its dropdowns open (opening one focuses the panel). MUI and
        // Ant place a dropdown once and move it only on a window resize, so
        // without this it stays where the old panel edge was.
        if (e.target === e.currentTarget && e.propertyName === "width") window.dispatchEvent(new Event("resize"));
      }}
      onClickCapture={e => {
        // Any link click while in fullscreen exits fullscreen, so the
        // navigation target is visible instead of hidden under the chat
        // overlay. Capture phase so a link's own handler can't swallow the
        // click, and so the persisted store is updated before a
        // target="_blank" tab opens (a new tab rehydrates the store and
        // would otherwise come up fullscreen too).
        if (
          isFullscreen &&
          e.target instanceof Element &&
          e.target.closest("a[href]")
        ) {
          setIsFullscreen(false);
        }
      }}
      onFocus={() => {
        setIsFocused(true);
      }}
      onBlur={() => {
        // Fullscreen is never exited on blur. Blur-based auto-exit was
        // inherently fragile: every Ant/MUI portal (Popover, Popconfirm,
        // Modal, Tabs, Select dropdown, …) lives outside the sidebar DOM
        // tree, so interacting with them triggered a blur and a spurious
        // fullscreen exit. The previous allowlist of portal class names was
        // a whack-a-mole. Fullscreen exits only on deliberate signals: the
        // toggle button, switching the sidebar tab, clicking a link, a route
        // change, or clicking the navbar / left-nav chrome (see effects
        // above).
        // Clicking outside the browser window blurs the focused element too,
        // but the user has not left the panel: shrinking it then left any
        // open dropdown at the old width. Focus comes back to the same place.
        if (!document.hasFocus()) return;
        setIsFocused(false);
      }}
    >
      <div className="main-sidebar__header ">
        <button
          className="main-sidebar__toggle-btn"
          onClick={e => {
            dispatch(toggleExpandedFavoritesSidebar());
          }}
          type="button"
        >
          <i className={`fa fa-caret-${caretDirection}`} aria-hidden="true" />
        </button>

        <div className="main-sidebar__tabs">
          <div
            id="lists"
            data-tab="lists"
            className="main-sidebar__tabs-tab--active main-sidebar__tabs-tab d-flex justify-between mb-0"
          >
            <StyledListHeaderDiv>
              {Pannels.length > 1 && (
                <Radio.Group
                  data-cy="sidebar-tab-group"
                  value={currentPanel}
                  onChange={e => {
                    setCurrentPanel(e.target.value);
                    if (e.target.value !== PannelEnum.RangerChat) {
                      setIsFullscreen(false);
                    }
                  }}
                >
                  {Pannels.map(({ key, title }) => (
                    <Radio.Button
                      key={key}
                      value={key}
                      data-cy={"sidebar-tab-" + key}
                      // MUI's small ToggleButton (~41px tall) is chunkier than the
                      // antd Radio.Button it replaced; trim vertical padding AND the
                      // loose body line-height (24.5px for 14px text) so the header
                      // toggle is a tight ~26px segmented control, freeing chat room.
                      style={{ padding: "2px 12px", lineHeight: 1.4 }}
                    >
                      {title}
                    </Radio.Button>
                  ))}
                </Radio.Group>
              )}
              {Pannels.length === 1 && <h4>{current?.title}</h4>}
              {currentPanel === PannelEnum.RangerChat && favoritesSidebarExpanded && (
                <Tooltip title={isFullscreen ? "Exit fullscreen" : "Fullscreen"}>
                  <Button
                    data-cy="sidebar-fullscreen-toggle"
                    size="small"
                    type="text"
                    // shape="circle" -> compact icon button. Without it the wrapper
                    // renders an empty MUI text Button whose default 64px min-width
                    // made the expand affordance look oversized next to the toggle.
                    shape="circle"
                    onClick={toggleFullscreen}
                    icon={
                      isFullscreen ? (
                        <CloseFullscreenIcon fontSize="small" sx={{ color: "primary.main" }} />
                      ) : (
                        <OpenInFullIcon fontSize="small" sx={{ color: "primary.main" }} />
                      )
                    }
                    style={{ marginLeft: 4 }}
                  />
                </Tooltip>
              )}
            </StyledListHeaderDiv>

            {current?.button && (
              <Button
                onClick={() => {
                  setAction(true);
                }}
                className="ml-auto"
                size="small"
                type="text"
                shape="circle"
                icon={
                  <Icon
                    component={() => (
                      // legacy icon-font glyph has no intrinsic size and rendered
                      // large + heavy in a filled circle; size it down and use a
                      // light (text) button tinted primary so it reads as a subtle
                      // add affordance, not a big solid blue disc.
                      <i
                        className={current?.button?.iconClass}
                        style={{ fontSize: 13, color: theme.palette.primary.main }}
                      />
                    )}
                  />
                }
              />
            )}
          </div>
        </div>
      </div>
      {current?.body({ buttonAction: action, setButtonAction: setAction })}
    </div>
  );
}

export default SideBar;
